import {
  Check,
  ChevronDown,
  Circle,
  CircleArrowRight,
  CircleCheck,
  CircleX,
  ListChecks
} from "lucide-react";
import { useMemo, useState, type FC } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import { usePlanActions } from "@/features/agents/artifacts/PlanActionsContext";
import { cn } from "@/lib/utils";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { PLAN_ACCENT_TEXT } from "../../plan/theme";
import type { PlanTodo } from "../../types";
import { useBuildProgress } from "../runEvents";
import { buildOf, buildStep } from "../runTracking";
import type { CaseAgentUIMessage } from "../useCaseAgentChat";

type Props = {
  /** The user message that started the build (`metadata.build`). */
  message: Pick<CaseAgentUIMessage, "id" | "role" | "metadata">;
  highlighted?: boolean;
  className?: string;
};

/** One todo's glyph in the expanded list. */
const TodoMark: FC<{ status: PlanTodo["status"] }> = ({ status }) => {
  const cls = "size-3.5 shrink-0";
  if (status === "in_progress")
    return (
      <CircleArrowRight
        className={cn(cls, PLAN_ACCENT_TEXT)}
        aria-label="In progress"
      />
    );
  if (status === "completed")
    return (
      <CircleCheck
        className={cn(cls, "text-muted-foreground")}
        aria-label="Completed"
      />
    );
  if (status === "cancelled")
    return <CircleX className={cn(cls, "text-fg-3")} aria-label="Cancelled" />;
  return <Circle className={cn(cls, "text-fg-3")} aria-label="Pending" />;
};

/**
 * What the transcript shows where a plan was sent to build, Cursor's Build
 * card: the `Build` label with the plan's title, then the todo being worked
 * with its position (`1/20`); the row expands to the whole list. The prompt
 * that actually went to the agent is never shown. Progress comes from the
 * transcript itself (the `update_todos` results after this message), so it
 * moves live and reads the same after a reload.
 */
export const BuildCard: FC<Props> = ({ message, highlighted, className }) => {
  const live = useBuildProgress(message.id);
  const stamped = useMemo(() => buildOf(message), [message]);
  const ide = useCaseIdeOptional();
  const actions = usePlanActions();
  const openPlan = ide
    ? (id: string, title: string) => ide.open({ kind: "plan", id, title })
    : actions?.openPlan ?? null;
  const [open, setOpen] = useState(false);

  const planId = live?.planId ?? stamped?.planId ?? null;
  const title =
    live?.title ?? stamped?.title ?? message.metadata?.build?.title ?? "Plan";
  const todos = live?.todos ?? stamped?.todos ?? [];
  const step = buildStep(todos);
  const hasTodos = todos.length > 0;
  const finished = step.finished || live?.status === "complete";
  const active = step.current?.status === "in_progress";

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      id={`msg-${message.id}`}
      className={cn(
        "w-full rounded-lg border border-line-2 bg-background text-sm text-foreground transition-colors",
        highlighted && "border-warn-line bg-warn-soft",
        className
      )}
      data-testid="build-card"
      data-plan-id={planId ?? undefined}
      data-finished={finished ? "true" : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">Build</span>
        <ListChecks
          className={cn("size-3.5 shrink-0", PLAN_ACCENT_TEXT)}
          aria-hidden
        />
        {planId && openPlan ? (
          <button
            type="button"
            className="min-w-0 flex-1 truncate text-left text-base text-foreground hover:underline"
            onClick={() => openPlan(planId, title)}
            title={title}
            data-testid="build-card-title"
          >
            {title}
          </button>
        ) : (
          <span
            className="min-w-0 flex-1 truncate text-base text-foreground"
            title={title}
            data-testid="build-card-title"
          >
            {title}
          </span>
        )}
      </div>
      {hasTodos ? (
        <>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 border-t border-line-2 px-3 py-1.5 text-left hover:bg-fill-1"
              data-testid="build-card-step"
              aria-expanded={open}
            >
              <span className="inline-flex size-3.5 shrink-0 items-center justify-center">
                {open ? (
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                ) : finished ? (
                  <Check
                    className="size-3.5 text-muted-foreground"
                    strokeWidth={2.5}
                  />
                ) : active ? (
                  <Spinner className={cn("size-3", PLAN_ACCENT_TEXT)} />
                ) : (
                  <Circle className="size-3.5 text-fg-3" />
                )}
              </span>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate",
                  finished ? "text-muted-foreground" : "text-foreground"
                )}
                data-testid="build-card-current"
              >
                {finished ? "All to-dos done" : step.current?.content ?? title}
              </span>
              <span
                className="shrink-0 tabular-nums text-muted-foreground"
                data-testid="build-progress"
              >
                {step.position}/{step.total}
              </span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul
              className="space-y-0.5 px-3 pt-0.5 pb-2"
              data-testid="build-card-todos"
            >
              {todos.map((todo) => {
                const done = todo.status === "completed";
                const cancelled = todo.status === "cancelled";
                const working = todo.status === "in_progress";
                return (
                  <li
                    key={todo.id}
                    data-testid={`todo-${todo.id}`}
                    data-status={todo.status}
                    className="flex items-start gap-2 py-0.5 leading-[1.5]"
                  >
                    <span className="mt-0.5 inline-flex shrink-0">
                      <TodoMark status={todo.status} />
                    </span>
                    <span
                      className={cn(
                        "min-w-0 flex-1 break-words",
                        done && "text-muted-foreground",
                        cancelled &&
                          "text-muted-foreground italic line-through",
                        working ? "text-foreground" : "text-fg-2"
                      )}
                    >
                      {todo.content}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CollapsibleContent>
        </>
      ) : null}
    </Collapsible>
  );
};
